import {
  SearchDatasetResult,
  SearchModelResult,
  SearchResponse,
  SearchTableResult,
} from "./appSearchApi";

export type SearchRowItem =
  | { kind: "model"; model: SearchModelResult }
  | { kind: "table"; table: SearchTableResult }
  | { kind: "dataset"; dataset: SearchDatasetResult };

export const rowKey = (item: SearchRowItem): string => {
  switch (item.kind) {
    case "model":
      return `model:${item.model.id}`;
    case "table":
      return `table:${item.table.id}`;
    case "dataset":
      return `dataset:${item.dataset.id}`;
  }
};

export const optionDomId = (item: SearchRowItem): string =>
  `app-search-opt-${rowKey(item)}`;

/** Server order is authoritative; buckets flatten models → tables →
 *  datasets. Must stay in sync with the render order in SearchResultsPanel so
 *  keyboard nav matches the visual order. */
export const flattenResults = (
  results: SearchResponse | null,
): SearchRowItem[] => {
  if (!results) return [];
  return [
    ...results.models.map((model): SearchRowItem => ({ kind: "model", model })),
    ...results.tables.map((table): SearchRowItem => ({ kind: "table", table })),
    ...results.datasets.map((dataset): SearchRowItem => ({
      kind: "dataset",
      dataset,
    })),
  ];
};

/** Quiet-lock: no parent-model view ⇔ no data view (parent-lock carve-out). */
export const isLockedTable = (table: SearchTableResult): boolean =>
  table.parent_model.name === null;

export const openTargetFor = (item: SearchRowItem): string | null => {
  switch (item.kind) {
    case "model":
      return `/canvas/${item.model.id}`;
    case "table":
      return isLockedTable(item.table)
        ? null
        : `/canvas/${item.table.parent_model.id}?table=${item.table.id}`;
    case "dataset":
      return `/datasets?dataset=${encodeURIComponent(item.dataset.id)}`;
  }
};

export const openSearchRow = (item: SearchRowItem): void => {
  const target = openTargetFor(item);
  if (target) window.open(target, "_blank");
};

/** Navigate to a search result in the current tab. A new tab stays available
 *  through the row's explicit open-in-new-tab button (`openSearchRow`).
 *  A refused open (no access, open in another tab) bounces back. */
export const navigateSearchRow = (
  item: SearchRowItem,
  navigate: (path: string) => void,
): void => {
  const target = openTargetFor(item);
  if (target) navigate(target);
};
